import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  BottomNavigation, 
  BottomNavigationAction, 
  AppBar, 
  Toolbar, 
  IconButton,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  Home as HomeIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  ExitToApp as LogoutIcon,
  Receipt as BillingIcon,
  Description as ReportIcon,
  LocalShipping as TransportationIcon,
  ManageAccounts as BillingAgentIcon,
  ReportProblem as UnusualIncidentIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { Provider } from '../types';
import { defaultBillingAgentProviderId } from '../utils/portalAccess';
import { authService } from '../services/auth';
import AppDateLocalization from './AppDateLocalization';

interface LayoutUser {
  attributes: {
    email: string;
    'custom:role': string;
    'custom:provider_id': string;
    'custom:user_id': string;
    'custom:admin_flag': string;
    'custom:billing_agent_flag': string;
    'custom:billing_agent_id': string;
    'custom:accessible_provider_ids'?: string;
    'custom:is_active': string;
    'custom:is_verified': string;
    given_name: string;
    family_name: string;
  };
}

interface LayoutProps {
  children: React.ReactNode;
  signOut: (() => void) | undefined;
  selectedProvider?: number | null;
  onProviderChange?: (providerId: number | null) => void;
  user?: LayoutUser | null;
}

const Layout: React.FC<LayoutProps> = ({ children, signOut, selectedProvider, onProviderChange, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [providers, setProviders] = useState<Provider[]>([]);

  const isAdmin = user?.attributes['custom:admin_flag'] === 'true';
  const isBillingAgent = user?.attributes['custom:billing_agent_flag'] === 'true';

  useEffect(() => {
    fetchProviders();
  }, [user]);

  useEffect(() => {
    if (isBillingAgent && !isAdmin && onProviderChange && providers.length > 0) {
      const accessibleIds = providers.map((p) => Number(p.provider_id)).filter((id) => id > 0);
      const currentId = selectedProvider != null ? Number(selectedProvider) : null;
      if (currentId == null || !accessibleIds.includes(currentId)) {
        const defaultId = defaultBillingAgentProviderId(null, accessibleIds);
        if (defaultId != null) {
          onProviderChange(defaultId);
        }
      }
    }
  }, [providers, selectedProvider, isBillingAgent, isAdmin, onProviderChange]);

  const fetchProviders = async () => {
    try {
      const data: Provider[] = await api.providers.getAll();
      setProviders(data);
      if (isBillingAgent && !isAdmin && onProviderChange) {
        const accessibleIds = data.map((p) => Number(p.provider_id)).filter((id) => id > 0);
        if (accessibleIds.length === 0) {
          if (selectedProvider != null) {
            onProviderChange(null);
          }
        }
        try {
          await authService.refreshUserProfile();
        } catch {
          // Non-fatal: provider list is already fresh from the API
        }
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const navRoutes = React.useMemo(() => {
    const routes = ['/'];
    if (isAdmin) routes.push('/billing-agents');
    routes.push(
      '/providers',
      '/clients',
      '/isp',
      '/visits',
      '/transportation',
      '/unusual-incidents',
      '/billing',
      '/compliance-reporting'
    );
    return routes;
  }, [isAdmin]);

  const getActiveTab = () => {
    const idx = navRoutes.indexOf(location.pathname);
    return idx >= 0 ? idx : 0;
  };

  const handleProviderChange = (event: SelectChangeEvent<number | ''>) => {
    const value = event.target.value;
    const providerId = value === '' ? null : Number(value);
    onProviderChange?.(providerId);
  };

  const handleNavChange = (_: React.SyntheticEvent, newValue: number) => {
    if (navRoutes[newValue]) {
      navigate(navRoutes[newValue]);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#BFD3EA',
      }}
    >
      <AppBar position="static" sx={{ backgroundColor: '#2D4B73' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img src="/logo-bar.png" alt="EasyIP Logo" style={{ height: '50px', maxWidth: '100%', objectFit: 'contain' }} />
          </Box>
          <FormControl size="small" sx={{ minWidth: 200, mr: 2 }}>
            <Select
              value={selectedProvider || ''}
              onChange={handleProviderChange}
              displayEmpty
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '& .MuiSelect-icon': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }
              }}
            >
              {isAdmin && <MenuItem value="">All Providers</MenuItem>}
              {!isAdmin && providers.length === 0 && (
                <MenuItem value="" disabled>No providers assigned</MenuItem>
              )}
              {providers.map((provider) => (
                <MenuItem key={provider.provider_id} value={Number(provider.provider_id)}>
                  {provider.first_name} {provider.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton color="inherit" onClick={signOut}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, backgroundColor: '#BFD3EA' }}>
        <AppDateLocalization>{children}</AppDateLocalization>
      </Box>

      <Paper elevation={3} sx={{ flexShrink: 0, pb: 'env(safe-area-inset-bottom)' }}>
        <BottomNavigation
          value={getActiveTab()}
          showLabels
          onChange={handleNavChange}
          sx={{
            height: 'auto',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              maxWidth: 'none',
              flex: '1 1 0',
              overflow: 'hidden',
              padding: '6px 2px 8px',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              fontWeight: 500,
              opacity: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            },
          }}
        >
          <BottomNavigationAction label="Home" icon={<HomeIcon />} />
          {isAdmin && (
            <BottomNavigationAction label="Billing Agents" icon={<BillingAgentIcon />} />
          )}
          <BottomNavigationAction label="Providers" icon={<BusinessIcon />} />
          <BottomNavigationAction label="Clients" icon={<PeopleIcon />} />
          <BottomNavigationAction label="ISP" icon={<AssessmentIcon />} />
          <BottomNavigationAction label="Visits" icon={<EventIcon />} />
          <BottomNavigationAction label="Transport" icon={<TransportationIcon />} />
          <BottomNavigationAction label="UI" icon={<UnusualIncidentIcon />} />
          <BottomNavigationAction label="Billing" icon={<BillingIcon />} />
          <BottomNavigationAction label="Compliance" icon={<ReportIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default Layout;
